import Link from "next/link";
import { Icon } from "@iconify/react";
import { BillingInfo } from "./types";
import { CatalogPlan } from "@/services/stripe";
import { formatCount, formatDay, formatMoney } from "@/utils/billingFormat";
import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { ButtonSpinner } from "@/components/ui/Button";
import { WARN_AT } from "./UsageStat";

interface Props {
  info: BillingInfo;
  catalog: CatalogPlan[];
  onKeepPlan: () => void;
  onReactivate: () => void;
  onOpenPortal: () => void;
  busy: "keep" | "reactivate" | "portal" | null;
}

const statusPill = (info: BillingInfo): { text: string; variant: BadgeVariant } | null => {
  const s = info.subscription;
  if (info.currentPlan.name === "Free") return null;
  if (s?.status === "past_due") return { text: "Payment overdue", variant: "error" };
  if (s?.cancelAtPeriodEnd || s?.status === "canceling") return { text: "Ending", variant: "warning" };
  return { text: "Active", variant: "success" };
};

/**
 * Where the account stands: plan, what happens next, credits left and the card on
 * file. Usage history and proxy bandwidth live on the Usage page; this only shows
 * the credit balance, because the low-credit emails send people here.
 */
export default function BillingSummary({ info, catalog, onKeepPlan, onReactivate, onOpenPortal, busy }: Props) {
  const plan = info.currentPlan;
  const sub = info.subscription;
  const isFree = plan.name === "Free";
  const catalogPlan = catalog.find(p => p.name === plan.name);
  const pill = statusPill(info);
  const ending = !!sub && (sub.cancelAtPeriodEnd || sub.status === "canceling");
  const pastDue = sub?.status === "past_due";
  const pending = sub?.pendingPlan ?? null;
  const pendingCatalog = pending ? catalog.find(p => p.name === pending.name) : null;

  const limit = plan.creditLimit ?? 0;
  const used = info.usage.creditsUsed;
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  // Same thresholds as the usage nudge and UsageStat: amber from 80%, red when nothing is left.
  const barColor = pct >= 100 ? "bg-danger" : pct >= WARN_AT ? "bg-warning" : "bg-primary";

  let next: React.ReactNode = null;
  if (isFree) next = <>Choose a plan below to get credits every month.</>;
  else if (pastDue) next = <span className="text-danger-text">Your last payment didn&apos;t go through. Stripe will retry it. Update your card within 3 days of the failed payment, or new jobs pause until you do.</span>;
  else if (ending) next = <>Ends on <strong className="font-medium text-fg">{formatDay(sub?.renewsAt)}</strong>, then moves to Free. You won&apos;t be charged again.</>;
  else if (pending)
    next = (
      <>
        Moves to <strong className="font-medium text-fg">{pending.name}</strong> on{" "}
        <strong className="font-medium text-fg">{formatDay(pending.effectiveAt)}</strong>
        {pendingCatalog ? ` (${formatMoney(pendingCatalog.priceCents, pendingCatalog.currency)} per month)` : ""}.
      </>
    );
  else if (sub?.renewsAt)
    next = (
      <>
        Renews on <strong className="font-medium text-fg">{formatDay(sub.renewsAt)}</strong>
        {sub.nextAmountCents != null ? ` for ${formatMoney(sub.nextAmountCents, sub.currency ?? "usd")}` : ""}.
      </>
    );

  const action = pastDue
    ? { label: "Update your card", onClick: onOpenPortal, loading: busy === "portal" }
    : ending
      ? { label: `Keep my ${plan.name} plan`, onClick: onReactivate, loading: busy === "reactivate" }
      : pending
        ? { label: `Stay on ${plan.name}`, onClick: onKeepPlan, loading: busy === "keep" }
        : null;

  const label = "text-13 font-medium text-fg-muted";
  const resetText = isFree
    ? "One-time credits. They don't reset."
    : ending
      ? `Yours until ${formatDay(sub?.renewsAt)}.`
      : sub?.renewsAt
        ? `Resets on ${formatDay(sub.renewsAt)}.`
        : "Resets each billing cycle.";

  return (
    <section aria-label="Plan summary" className="bg-surface border border-line rounded-card shadow-card grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-line">
      {/* Plan */}
      <div className="p-6 space-y-2">
        <p className={label}>Plan</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xl font-semibold text-fg">{plan.name}</span>
          {pill && <Badge variant={pill.variant} label={pill.text} />}
        </div>
        {!isFree && catalogPlan && <p className="text-sm text-fg-muted">{formatMoney(catalogPlan.priceCents, catalogPlan.currency)} per month</p>}
        {next && <p className="text-sm text-fg-secondary">{next}</p>}
        {action && (
          <button
            onClick={action.onClick}
            disabled={action.loading}
            type="button"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-text hover:underline cursor-pointer disabled:opacity-60"
          >
            {action.loading && <ButtonSpinner />}
            {action.label}
          </button>
        )}
      </div>

      {/* Credits */}
      <div className="p-6 space-y-2">
        <p className={label}>Credits</p>
        <p className="text-xl font-semibold text-fg tabular-nums">
          {formatCount(Math.max(0, limit - used))} <span className="text-sm font-normal text-fg-muted">left of {formatCount(limit)}</span>
        </p>
        <div className="w-full h-2 rounded-full bg-surface-muted ring-1 ring-inset ring-line overflow-hidden">
          <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${pct > 0 ? Math.max(2, pct) : 0}%` }} />
        </div>
        <p className="text-sm text-fg-muted">
          {resetText}
          {info.usage.additionalCredits > 0 && ` Includes ${formatCount(info.usage.additionalCredits)} top-up credits.`}{" "}
          <Link href="/dashboard/usage" className="font-medium text-primary-text hover:underline whitespace-nowrap">
            View usage
          </Link>
        </p>
      </div>

      {/* Payment */}
      <div className="p-6 space-y-2">
        <p className={label}>Payment method</p>
        {info.paymentMethod ? (
          <>
            <p className="flex items-center gap-2 text-sm text-fg">
              <Icon icon="solar:card-bold" className="text-xl text-fg-muted shrink-0" />
              <span>
                <span className="capitalize">{info.paymentMethod.brand}</span> ending {info.paymentMethod.last4}
              </span>
            </p>
            <p className="text-sm text-fg-muted">
              Expires {String(info.paymentMethod.expMonth).padStart(2, "0")}/{info.paymentMethod.expYear}
            </p>
            <button type="button" onClick={onOpenPortal} disabled={busy === "portal"} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-text hover:underline cursor-pointer disabled:opacity-60">
              {busy === "portal" && <ButtonSpinner />}
              Update card
            </button>
          </>
        ) : (
          <p className="text-sm text-fg-muted">{isFree ? "None needed on the Free plan." : "No card on file."}</p>
        )}
      </div>
    </section>
  );
}
